import cn from 'classnames'
import React from 'react'
import { useTranslation } from 'react-i18next'
import MainButton from '../common/util/input/MainButton'
import { formatUSD2, formatWAX, formatWAXS } from '../helpers/FormatLinks'
import { getBoostAction } from '../helpers/WaxApi'
import LoadingIndicator from '../loadingindicator'
import { useSharedState } from '../waxplorer/Waxplorer'

export const AuctionButtons = ({
    auction,
    bidPlaced,
    ended,
    canceled,
    className = 'w-full h-8 mt-auto',
    transferred,
    callBack,
    isLoading,
    onAdd,
    setIsLoading,
    showOwner,
    currentAsset,
    view,
    onOpen,
    setAction,
}) => {
    const [state, { login }] = useSharedState()

    const activeUser = state?.activeUser

    const userName = activeUser?.accountName
        ? state?.activeUser.accountName
        : null
    const { t } = useTranslation('common')

    const { startBid, seller, assets, currency, market, bids } = auction

    let { bidder, currentBid } = auction

    for (const bid of bids) {
        if (bid.bid >= currentBid) {
            currentBid = bid.bid
            bidder = bid.bidder
        }
    }

    const { blacklisted, owner } = assets[currentAsset]

    const claim = async () => {
        let { auctionId } = auction

        try {
            const result = await activeUser.session.transact(
                {
                    actions: [
                        getBoostAction(activeUser),
                        {
                            account: market,
                            name:
                                userName === bidder
                                    ? 'auctclaimbuy'
                                    : 'cancelauct',
                            authorization: [
                                {
                                    actor: userName,
                                    permission: activeUser.requestPermission
                                        ? activeUser.requestPermission
                                        : 'active',
                                },
                            ],
                            data: {
                                auction_id: auctionId,
                            },
                        },
                    ],
                },
                {
                    expireSeconds: 300,
                    blocksBehind: 0,
                },
            )
            callBack(
                {
                    success: true,
                    transactionId: result?.response?.transaction_id,
                },
                'claim',
            )
        } catch (e) {
            console.error(e)
            callBack(
                {
                    success: false,
                    error: e.message,
                },
                'claim',
            )
        } finally {
            setIsLoading(false)
        }
    }

    const cancel = async () => {
        let { auctionId } = auction

        try {
            const result = await activeUser.session.transact(
                {
                    actions: [
                        getBoostAction(activeUser),
                        {
                            account: market,
                            name: 'cancelauct',
                            authorization: [
                                {
                                    actor: userName,
                                    permission: activeUser.requestPermission
                                        ? activeUser.requestPermission
                                        : 'active',
                                },
                            ],
                            data: {
                                auction_id: auctionId,
                            },
                        },
                    ],
                },
                {
                    expireSeconds: 300,
                    blocksBehind: 0,
                },
            )
            callBack(
                {
                    success: true,
                    transactionId: result?.response?.transaction_id,
                },
                'cancel',
            )
        } catch (e) {
            console.error(e)
            callBack(
                {
                    success: false,
                    error: e.message,
                },
                'cancel',
            )
        } finally {
            setIsLoading(false)
        }
    }

    const bid = () => {
        setIsLoading(true)
        setAction('bid')
    }

    const buyContainerClassName = cn(
        'relative grid grid-cols-2 space-x-2 font-bold text-black dark:!text-white block w-full my-auto',
        { 'text-center': view !== 'full' },
        { 'text-left': view === 'full' },
    )
    const singleContainerClassName = cn(
        'mt-auto relative grid font-bold text-black dark:!text-white block text-center grid-cols-1 w-full my-auto',
    )
    const sellContainerClassName = cn(
        'mt-auto relative grid grid-cols-2 font-bold text-black dark:!text-white block w-full my-auto',
        { 'text-center': view !== 'full' },
        { 'text-left right-auto': view === 'full' },
    )

    const buttonClassName = cn(
        'font-bold rounded-md text-primary',
        { 'm-auto': view !== 'full' },
        { 'mr-auto': view === 'full' },
    )
    const cancelButtonClassName = cn(buttonClassName, 'm-auto w-24 text-white')

    const detailsButton =
        view === 'full' ? (
            ''
        ) : (
            <MainButton
                fullWidth={true}
                className={'font-bold rounded-md text-primary'}
                varient={'bordered'}
                onClick={onOpen}
                padding={'pr-2'}
            >
                {t('asset.details')}
            </MainButton>
        )

    const singleDetailsButton =
        view === 'full' ? (
            ''
        ) : (
            <MainButton
                fullWidth={true}
                className={'font-bold rounded-md text-primary'}
                varient={'bordered'}
                onClick={onOpen}
            >
                {t('asset.details')}
            </MainButton>
        )

    const bidField = (
        <div className={sellContainerClassName}>
            {detailsButton}
            <MainButton
                fullWidth={true}
                className={buttonClassName}
                onClick={bid}
                padding={'pl-2'}
            >
                {t('asset.bid')}
            </MainButton>
        </div>
    )

    const claimField = (
        <div className={sellContainerClassName}>
            {detailsButton}
            <MainButton
                fullWidth={true}
                className={buttonClassName}
                onClick={claim}
                padding={'pl-2'}
            >
                {t('asset.claim')}
            </MainButton>
        </div>
    )

    const detailsField = (
        <div className={singleContainerClassName}>{singleDetailsButton}</div>
    )

    const cancelField = (
        <div className={buyContainerClassName}>
            {detailsButton}
            <MainButton className={cancelButtonClassName} onClick={cancel}>
                {t('asset.cancel')}
            </MainButton>
        </div>
    )

    const loginField = (
        <div className={buyContainerClassName}>
            {detailsButton}
            <MainButton
                fullWidth={true}
                varient={'solid'}
                onClick={() => login()}
            >
                {t('navigation.login')}
            </MainButton>
        </div>
    )

    let biddable, claimable, cancable

    if (
        !ended &&
        userName &&
        userName !== seller &&
        !bidPlaced &&
        !transferred &&
        !canceled &&
        market
    ) {
        biddable = true
    } else if (
        owner !== userName &&
        ((ended && userName === bidder) ||
            (ended && userName === seller && !bidder)) &&
        !transferred &&
        !canceled
    ) {
        claimable = true
    } else if (
        userName &&
        !ended &&
        (userName === seller || !bidder) &&
        !canceled &&
        !transferred
    ) {
        cancable = true
    }

    return (
        <div className={cn('relative mt-4', className)}>
            <div
                className={cn(
                    'relative py-0 px-0 2xl:px-1 w-full text-base mb-6 text-zinc-900 dark:text-white',
                    'md:text-xs lg:text-base leading-5 flex-wrap text-center h-[28px] sm:h-[44px]',
                    { 'text-center': view !== 'full' },
                    { 'text-left': view === 'full' },
                )}
            >
                <div
                    className={cn(
                        'flex h-[20px] sm:h-2/3',
                        { 'justify-start mx-auto': view !== 'full' },
                        { 'justify-start mr-auto': view === 'full' },
                    )}
                >
                    {currency === 'USD' && (currentBid || startBid) ? (
                        <div
                            className={
                                'text-xs md:text-base lg:text-base xl:text-xs text-right my-auto font-bold'
                            }
                        >
                            <span className="hidden sm:block">
                                <b>
                                    {bidder
                                        ? 'Current Bid: '
                                        : 'Starting Bid: '}
                                    {formatUSD2(
                                        currentBid ? currentBid : startBid,
                                    )}
                                </b>
                            </span>
                        </div>
                    ) : (
                        ''
                    )}
                    {currency === 'WAX' && (currentBid || startBid) ? (
                        <div
                            className={
                                'text-xs md:text-base lg:text-base xl:text-xs text-right my-auto font-bold'
                            }
                        >
                            <span className="hidden sm:block">
                                <b>
                                    {bidder
                                        ? 'Current Bid: '
                                        : 'Starting Bid: '}
                                    {formatWAX(
                                        currentBid ? currentBid : startBid,
                                    )}
                                </b>
                            </span>
                            <span className="block sm:hidden">
                                <b>
                                    {bidder
                                        ? 'Current Bid: '
                                        : 'Starting Bid: '}
                                    {formatWAXS(
                                        currentBid ? currentBid : startBid,
                                    )}
                                </b>
                            </span>
                        </div>
                    ) : (
                        ''
                    )}
                </div>
            </div>

            <div
                className={'flex h-auto my-auto mx-auto w-full justify-center'}
            >
                {isLoading ? (
                    <LoadingIndicator
                        className={buttonClassName}
                        margin={'m-0'}
                    />
                ) : !blacklisted && !onAdd && claimable ? (
                    claimField
                ) : !blacklisted && !onAdd && biddable ? (
                    bidField
                ) : !blacklisted && !onAdd && cancable ? (
                    cancelField
                ) : !showOwner && !cancable ? (
                    detailsField
                ) : !claimable && !biddable && !cancable && !userName ? (
                    loginField
                ) : (
                    ''
                )}
            </div>
        </div>
    )
}

export default AuctionButtons
